import { Menu, X } from "lucide-react";
import { createContext, type ReactNode, useContext, useState } from "react";

type AppShellContextType = {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
};

const AppShellContext = createContext<AppShellContextType | null>(null);

export function useAppShell() {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be used within AppShell");
  }
  return context;
}

export type AppShellProps = {
  sidebar: ReactNode;
  navbar: ReactNode;
  children: ReactNode;
  mobileNav?: ReactNode;
  className?: string;
};

export function AppShell({ sidebar, navbar, children, mobileNav, className = "" }: AppShellProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <AppShellContext.Provider value={{ isSidebarOpen, toggleSidebar, closeSidebar }}>
      <div className={`flex h-screen bg-surface-950 ${className}`}>
        {/* Desktop sidebar - always visible on md+ */}
        <div className="hidden md:flex">{sidebar}</div>

        {/* Mobile nav — two-panel Discord-style drawer (when provided) */}
        {mobileNav
          ? mobileNav
          : isSidebarOpen && (
              <>
                <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={closeSidebar} />
                <div className="fixed inset-y-0 left-0 z-50 md:hidden">{sidebar}</div>
              </>
            )}

        {/* Main content wrapper */}
        <div className="flex flex-1 flex-col min-w-0">
          {navbar}
          <div className="flex-1 flex flex-col min-h-0">
            <div
              id="app-content-wrapper"
              className="relative flex flex-col flex-1 bg-surface-950 overflow-hidden mx-2 mb-2"
            >
              <main className="flex-1 overflow-auto bg-surface-900 border border-surface-800 rounded-xl">
                {children}
              </main>
            </div>
          </div>
        </div>
      </div>
    </AppShellContext.Provider>
  );
}

export function MobileMenuButton({ className = "" }: { className?: string }) {
  const { isSidebarOpen, toggleSidebar } = useAppShell();

  return (
    <button
      onClick={toggleSidebar}
      className={`md:hidden p-2 rounded-lg hover:bg-surface-850 transition-colors ${className}`}
    >
      {isSidebarOpen ? (
        <X className="w-5 h-5 text-white" />
      ) : (
        <Menu className="w-5 h-5 text-white" />
      )}
    </button>
  );
}
